import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { CourtPublic, AssetPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Pencil, Trash2, Eye, Box } from "lucide-react";
import { ListBackButton } from "@/components/list-back-button";

export default function CourtDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [court, setCourt] = useState<CourtPublic | null>(null);
  const [assets, setAssets] = useState<AssetPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    Promise.all([api.getCourt(id), api.getAssets(id)])
      .then(([courtRes, assetsRes]) => {
        setCourt(courtRes.court);
        setAssets(assetsRes.assets);
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load court",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleDeleteAsset(assetId: string) {
    if (!confirm("Are you sure you want to delete this asset?")) return;
    try {
      await api.deleteAsset(assetId);
      setAssets((prev) => prev.filter((a) => a.id !== assetId));
    } catch (e) {
      setError(
        e instanceof api.ApiError ? e.message : "Failed to delete asset",
      );
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="court-detail" data-state="loading">Loading...</p>;
  }

  if (error) {
    return <p className="text-sm text-destructive" data-page="court-detail" data-state="error" data-error>{error}</p>;
  }

  if (!court) {
    return <p className="text-muted-foreground" data-page="court-detail" data-state="empty">Court not found</p>;
  }

  const canEdit = court._actions.includes("edit");

  return (
    <div className="flex flex-col gap-6" data-page="court-detail" data-state="ready">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-region="header">
        <ListBackButton to="/courts" label="Courts" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{court.name}</h1>
          <p className="text-muted-foreground">{court.school_name}</p>
          <p className="text-sm text-muted-foreground">{court.court_type_name}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/courts/${court.id}/3d`}
            className={buttonVariants({ variant: "outline", size: "sm" })}
            data-action="3d-view"
          >
            <Box className="mr-2 h-4 w-4" />
            3D View
          </Link>
          {canEdit && (
            <Link
              to={`/courts/${court.id}/edit`}
              className={buttonVariants({ variant: "outline", size: "sm" })}
              data-action="edit"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit Court
            </Link>
          )}
        </div>
      </div>

      {/* Assets */}
      <div className="flex flex-col gap-4" data-region="assets">
        <h2 className="text-lg font-medium">Assets</h2>

        {assets.length === 0 && (
          <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
            <p className="text-muted-foreground">No assets yet</p>
          </div>
        )}

        {assets.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Ad Spaces</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow key={asset.id}>
                    <TableCell className="font-medium">
                      {asset.name ?? "Unnamed"}
                    </TableCell>
                    <TableCell>{asset.asset_type_name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {asset.position_zone ?? "\u2014"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {asset.ad_space_count}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Link
                          to={`/assets/${asset.id}`}
                          className={buttonVariants({ variant: "ghost", size: "sm" })}
                        >
                          <Eye className="h-4 w-4" />
                        </Link>
                        {asset._actions.can_delete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAsset(asset.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
